import api from '@core/services/api/_api';
import $modal from '@core/services/modal';
import util from '@core/services/util';
import { differenceInDays } from 'date-fns';
import settings from 'settings';

import { CustomerIdentification } from '@core/classes/CustomerIdentification';
import DocumentAssociation from '@core/classes/DocumentAssociation';
import ENUMS from '@core/classes/Enums';
import FIMenuAddress from '@core/classes/FIMenuAddress';
import { AddressType } from '@core/classes/SharedEnums';
import { redirectToCustomerPortal } from '@core/helpers/Customer/customer-helper';
import modalConfirmCustomerApplicationInfo from '@core/modals/customerApp/modalConfirmCustomerApplicationInfo.vue';
import modalCustomerApplicationInfo from '@core/modals/customerApp/modalCustomerApplicationInfo.vue';
import modalSelectCustomerApplicationInfo from '@core/modals/customerApp/modalSelectCustomerApplicationInfo.vue';
import creditProviderAPI from '@core/services/api/api.creditProvider';
import { vehicleAPI } from '@core/services/api/api.vehicle';
import { getDateInstance } from '@core/services/util.date';
import LogRocket from 'logrocket';
import Application from './Application';

export function resetDateCompleted(value: any, extraSaves: any, question: any, application: Application) {
	if (application.dateCompleted != null && application.status == ENUMS.CUSTOMER_APPLICATION_STATUS.CompletedNoCredit) {
		application.dateCompleted = null;
	}
}

export async function findPlaidCustomerRecord(value: any, extraSaves: any, question: any, application: Application) {
	if (!application.plaidId) {
		const documentAssociations = [
			new DocumentAssociation({
				type: ENUMS.DOC_ASSOCIATION.Customer,
				id: application.customerId,
			}),
		];
		const response = await api.plaid.getPastPlaidExchanges(documentAssociations, ENUMS.PLAID_INTERACTION.GetIdentityVerification);

		if (response && response.data && response.data.length > 0) {
			const plaidData = response.data.sort((a: any, b: any) => (a.timestamp < b.timestamp ? 1 : -1)).find((r: any) => r.identityVerification != null)
				.identityVerification.response;
			await plaidInfo(plaidData, extraSaves, question, application);
		}
	}
}

export async function plaidInfo(plaidData: any, extraSaves: any, question: any, application: Application) {
	if (plaidData && plaidData.clientUserId) {
		application.plaidId = plaidData.clientUserId;
		console.log('PlaidData', plaidData);

		const driverLicense = plaidData.documentaryVerification?.documents?.find((d: any) => d.extractedData?.category == 0)?.extractedData;
		if (driverLicense) {
			const driverLicenseObj = new CustomerIdentification({
				type: 1,
				idNumber: driverLicense.idNumber,
				jurisdiction: driverLicense.issuingRegion,
				expirationDate: driverLicense.expirationDate,
			});

			application.quoteAppData.applicationsDataAnswered.personalInfo.identifications.push(driverLicenseObj);
		}
	}
}

//TODO: In the future, we will refactor the action 1 on provideGenerationSuffixQuestion, so when the user clicks 'Back' on the question, it doesn't clear the typed answer.
export async function clearSuffixIfOther(suffix: any, extraSaves: any, question: any, application: Application) {
	const provideGenerationSuffixQuestion = application.quoteAppData.questions.find((q: any) => q.key == 'provideGenerationSuffix');

	if (provideGenerationSuffixQuestion?.savedAnswer == 'other') {
		application.quoteAppData.applicationsDataAnswered.personalInfo.generationSuffix = null;
		provideGenerationSuffixQuestion?.clearAnswer();
	}
}

export async function clearAddress(isUsOrTerritory: boolean, extraSaves: any, question: any, application: Application) {
	let questionsAndAnswers: any[] = [];
	const applicationsDataAnswered = application.quoteAppData.applicationsDataAnswered;
	const personalInfo = applicationsDataAnswered.personalInfo;
	console.log('personalInfo :>> ', personalInfo.currentResidence.country);

	switch (question.key) {
		case 'liveInsideUS':
			if ((isUsOrTerritory && personalInfo.currentResidence.country != 'US') || (!isUsOrTerritory && personalInfo.currentResidence.country == 'US')) {
				questionsAndAnswers = [
					{ questionKey: 'provideZipCode', dataKey: null },
					{ questionKey: 'provideApartment', dataKey: null },
					{ questionKey: 'provideStreetAddress', dataKey: null },
					{ questionKey: 'provideOutsideUSStreetAddress', dataKey: null },
					{ questionKey: 'provideCity', dataKey: null },
					{ questionKey: 'provideState', dataKey: null },
					{ questionKey: 'provideOutsideUSState', dataKey: null },
					{ questionKey: 'provideCounty', dataKey: null },
				];

				if (isUsOrTerritory) {
					questionsAndAnswers.push({ questionKey: 'provideUSTerritory', dataKey: 'US' });
				} else {
					questionsAndAnswers.push({ questionKey: 'provideUSTerritory', dataKey: null });
				}
			}
			break;
		case 'liveInsideUSPreviously':
			if ((isUsOrTerritory && personalInfo.previousResidence.country != 'US') || (!isUsOrTerritory && personalInfo.previousResidence.country == 'US')) {
				questionsAndAnswers = [
					{ questionKey: 'providePreviousZipCode', dataKey: null },
					{ questionKey: 'providePreviousApartment', dataKey: null },
					{ questionKey: 'providePreviousStreetAddress', dataKey: null },
					{
						questionKey: 'providePreviousOutsideUSStreetAddress',
						dataKey: null,
					},
					{ questionKey: 'providePreviousCity', dataKey: null },
					{ questionKey: 'providePreviousState', dataKey: null },
					{ questionKey: 'providePreviousOutsideUSState', dataKey: null },
				];

				if (isUsOrTerritory) {
					questionsAndAnswers.push({
						questionKey: 'providePreviousUSTerritory',
						dataKey: 'US',
					});
				} else {
					questionsAndAnswers.push({
						questionKey: 'providePreviousUSTerritory',
						dataKey: null,
					});
				}
			}
			break;
	}
	if (questionsAndAnswers.length > 0) {
		updateData(questionsAndAnswers, application);
	}
}

export async function setUSTerritory(territory: any, extraSaves: any, question: any, application: Application) {
	if (territory && territory != 'US') {
		if (question.key == 'provideStreetAddress') {
			application.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.state = territory;
		} else {
			application.quoteAppData.applicationsDataAnswered.personalInfo.previousResidence.state = territory;
		}
	}
}

export async function updateSelectedAddress(extraSaves: any, question: any, application: Application, searchText: string) {
	if (extraSaves.zip == null && extraSaves.state == null && extraSaves.county == null) {
		let questionsAndAnswers: any[] = [];
		let address1 = extraSaves.address1 ?? null;

		if (question.key == 'provideStreetAddress') {
			const territory = application.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.usTerritory;
			const state = territory && territory != 'US' ? territory : null;

			questionsAndAnswers = [
				{ questionKey: 'provideCity', dataKey: null },
				{ questionKey: 'provideState', dataKey: state },
				{ questionKey: 'provideCounty', dataKey: null },
			];
			if (address1) {
				questionsAndAnswers.push({ questionKey: 'provideStreetAddress', dataKey: address1 });
			}
		} else {
			const territory = application.quoteAppData.applicationsDataAnswered.personalInfo.previousResidence.usTerritory;
			const state = territory && territory != 'US' ? territory : null;

			questionsAndAnswers = [
				{ questionKey: 'providePreviousCity', dataKey: null },
				{ questionKey: 'providePreviousState', dataKey: state },
				{ questionKey: 'providePreviousCounty', dataKey: null },
			];
			if (address1) {
				questionsAndAnswers.push({ questionKey: 'providePreviousStreetAddress', dataKey: address1 });
			}
		}

		updateData(questionsAndAnswers, application);

		return searchText;
	} else {
		const address1 = extraSaves.address1;
		await saveSelectedAddressValues(extraSaves, question, application);
		return address1;
	}
}

async function saveSelectedAddressValues(addressDetails: any, question: any, application: Application) {
	let questionsAndAnswers: any[] = [];

	switch (question.key) {
		case 'provideStreetAddress': {
			const usTerritory = application.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence.usTerritory;
			const state = usTerritory && usTerritory != 'US' ? usTerritory : addressDetails.state;

			questionsAndAnswers = [
				{ questionKey: 'provideZipCode', dataKey: addressDetails.zip ?? null },
				{ questionKey: 'provideStreetAddress', dataKey: addressDetails.address1 },
				{ questionKey: 'provideCity', dataKey: addressDetails.city ?? null },
				{ questionKey: 'provideState', dataKey: state ?? null },
				{ questionKey: 'provideCounty', dataKey: addressDetails.county ?? null },
			];
			break;
		}
		case 'providePreviousStreetAddress': {
			const usTerritory = application.quoteAppData.applicationsDataAnswered.personalInfo.previousResidence.usTerritory;
			const state = usTerritory && usTerritory != 'US' ? usTerritory : addressDetails.state;

			questionsAndAnswers = [
				{
					questionKey: 'providePreviousZipCode',
					dataKey: addressDetails.zip,
				},
				{ questionKey: 'providePreviousStreetAddress', dataKey: addressDetails.address1 },
				{
					questionKey: 'providePreviousCity',
					dataKey: addressDetails.city,
				},
				{ questionKey: 'providePreviousState', dataKey: state },
				{
					questionKey: 'providePreviousCounty',
					dataKey: addressDetails.county,
				},
			];
			break;
		}
	}

	let zipCode = null;
	const zipCodeKey = questionsAndAnswers.find(d => d.dataKey == addressDetails.zip).questionKey;
	const zipCodeQuestion = application.quoteAppData.questions.find((q: any) => q.key == zipCodeKey);
	zipCode = zipCodeQuestion.getValue(zipCodeQuestion.dataStructurePath);

	if (addressDetails.zip && addressDetails.zip.trim() != zipCode.slice(0, 5)?.trim()) {
		let title = null;
		let message = null;
		if (application.quoteAppData.language == 'English') {
			title = 'Zip Code Discrepancy';
			message = 'The zip code entered is different than the zip code we found. Please select which zip code is the correct one that should be used.';
		} else {
			title = 'Discrepancia en código postal';
			message = 'El código postal ingresado es diferente al código postal que encontramos. Por favor seleccione el código postal correcto que debemos usar.';
		}
		//call modal
		$modal.open(modalConfirmCustomerApplicationInfo, {
			name: 'modalConfirmCustomerApplicationInfo',
			passedData: {
				title: title,
				info: message,
				alternativeValueText: addressDetails.zip,
				currentValueText: zipCode,
			},
			backdrop: false,
			postFunction: () => {
				updateData(
					questionsAndAnswers.filter(d => d.dataKey == addressDetails.zip),
					application,
				);
			},
		});
	}

	updateData(
		questionsAndAnswers.filter(d => d.dataKey != addressDetails.zip),
		application,
	);
}

function updateData(data: any, application: any) {
	data.forEach((d: any) => {
		const questionToUpdate = application.quoteAppData.questions.find((q: any) => q.key == d.questionKey);
		if (questionToUpdate) {
			questionToUpdate.savedAnswer = d.dataKey;
			questionToUpdate.setValue(d.dataKey);
		}
	});
}

// Confirming address based on the USPS address suggestions
export async function confirmAddress(county: string, extraSaves: any, question: any, application: Application) {
	// Get the address being verified
	const addressBeingVerified =
		question.key == 'provideCounty'
			? application.quoteAppData.applicationsDataAnswered.personalInfo.currentResidence
			: application.quoteAppData.applicationsDataAnswered.personalInfo.previousResidence;

	// If the address is not in the US, we can not confirm the address with USPS
	if (addressBeingVerified.country != 'US') {
		return;
	}

	// Get the first address question key
	// Create the FIMenuAddress object for address being verified
	let fiMenuAddress = new FIMenuAddress();
	fiMenuAddress.address = addressBeingVerified.addressLine1;
	fiMenuAddress.addressExtra = addressBeingVerified.addressLine2;
	fiMenuAddress.city = addressBeingVerified.city;
	fiMenuAddress.state = addressBeingVerified.state;
	fiMenuAddress.zip = addressBeingVerified.zipCode;
	fiMenuAddress.addressType = AddressType.Customer;

	// Call the USPS API to get address suggestions
	const uspsSuggestionsResponse = await api.address.getStandardAddress(fiMenuAddress);

	// Check if the USPS API returned an address
	if (uspsSuggestionsResponse.data?.address != null) {
		// Check if the suggested address is an exact match to the address being verified
		const isSuggestionExactMatch =
			uspsSuggestionsResponse.data.address.streetAddress ==
				(addressBeingVerified.addressLine1 + (addressBeingVerified.addressLine2 != null ? ' ' + addressBeingVerified.addressLine2 : '')).toUpperCase() &&
			uspsSuggestionsResponse.data.address.city == addressBeingVerified.city.toUpperCase() &&
			uspsSuggestionsResponse.data.address.state == addressBeingVerified.state.toUpperCase() &&
			uspsSuggestionsResponse.data.address.zipCode == addressBeingVerified.zipCode;

		// If the suggested address is not an exact match, show the address confirmation modal
		if (!isSuggestionExactMatch) {
			let title = null;
			let message = null;
			let currentValueTitle = null;
			let alternativeValuesTitle = null;

			// Set the title, message, current value title, and alternative values title based on the language
			if (application.quoteAppData.language == 'English') {
				title = 'Address Discrepancy';
				message = 'We found a more accurate address based on USPS records. Using the suggested address helps avoid delivery or verification issues';
				currentValueTitle = 'Keep my original address';
				alternativeValuesTitle = 'Use recommended address (preferred)';
			} else {
				title = 'Discrepancia en dirección';
				message =
					'Encontramos una dirección mas precisa basada en los registros de USPS. Usar la dirección sugerida ayuda a evitar problemas de entrega o verificación.';
				currentValueTitle = 'Mantener mi dirección original';
				alternativeValuesTitle = 'Usar dirección sugerida (preferida)';
			}

			// Get the current and alternative addresses to display
			const currentAddressToDisplay = [
				(addressBeingVerified.addressLine1 + (addressBeingVerified.addressLine2 != null ? ' ' + addressBeingVerified.addressLine2 : '')).toUpperCase(),
				addressBeingVerified.city.toUpperCase() + ', ' + addressBeingVerified.state.toUpperCase() + ' ' + addressBeingVerified.zipCode,
			];
			const alternativeAddressesToDisplay = [
				uspsSuggestionsResponse.data.address.streetAddress.toUpperCase(),
				uspsSuggestionsResponse.data.address.city.toUpperCase() +
					', ' +
					uspsSuggestionsResponse.data.address.state.toUpperCase() +
					' ' +
					uspsSuggestionsResponse.data.address.zipCode,
			];

			await new Promise<void>(resolve => {
				let settled = false;
				const finish = () => {
					if (settled) return;
					settled = true;
					resolve();
				};
				// Open the address confirmation modal; resolve when the user confirms or keeps the current address
				$modal.open(modalSelectCustomerApplicationInfo, {
					name: 'modalSelectCustomerApplicationInfo',
					passedData: {
						title: title,
						info: message,
						alternativeValuesTitle: alternativeValuesTitle,
						alternativeValues: alternativeAddressesToDisplay,
						currentValueTitle: currentValueTitle,
						currentValue: currentAddressToDisplay,
						cancelFunction: finish,
					},
					backdrop: false,
					postFunction: () => {
						updateFullAddressFromUSPS(uspsSuggestionsResponse.data.address, question, application);
						finish();
					},
				});
			});
		}
	} else {
		// No address suggestions found, need to confirm the address
		let title = null;
		let message = null;
		if (application.quoteAppData.language == 'English') {
			title = 'Address Discrepancy';
			message = 'We were unable to find a match for you address in USPS records. Would you like to try again?';
		} else {
			title = 'Discrepancia en dirección';
			message = 'No pudimos encontrar una dirección exacta en los registros de USPS. ¿Desea intentarlo nuevamente?';
		}

		question.redoQuestions = false;

		await new Promise<void>(resolve => {
			let settled = false;
			const finish = () => {
				if (settled) return;
				settled = true;
				resolve();
			};

			$modal.open(modalConfirmCustomerApplicationInfo, {
				name: 'modalConfirmCustomerApplicationInfo',
				passedData: {
					title: title,
					info: message,
					alternativeValueText: 'Yes',
					currentValueText: 'No',
					cancelFunction: finish,
				},
				backdrop: false,
				postFunction: () => {
					question.redoQuestions = true;
					finish();
				},
			});
		});
	}

	return;
}

// Updating the full address from the USPS address suggestions
function updateFullAddressFromUSPS(uspsAddressSuggestions: any, question: any, application: Application) {
	let questionsAndAnswers: any[] = [];

	// Getting the trimmed street address
	const trimmedStreetAddress = stripTrailingSecondaryFromStreet(uspsAddressSuggestions.streetAddress, uspsAddressSuggestions.secondaryAddress);

	// Preparing the questions and answers to update based on the address type (current or previous)
	if (question.key == 'provideCounty') {
		questionsAndAnswers = [
			{ questionKey: 'provideZipCode', dataKey: uspsAddressSuggestions.zipCode },
			{ questionKey: 'provideStreetAddress', dataKey: trimmedStreetAddress },
			{ questionKey: 'provideApartment', dataKey: uspsAddressSuggestions.secondaryAddress },
			{ questionKey: 'provideCity', dataKey: uspsAddressSuggestions.city },
			{ questionKey: 'provideState', dataKey: uspsAddressSuggestions.state },
		];
	} else {
		questionsAndAnswers = [
			{ questionKey: 'providePreviousZipCode', dataKey: uspsAddressSuggestions.zipCode },
			{ questionKey: 'providePreviousStreetAddress', dataKey: trimmedStreetAddress },
			{ questionKey: 'providePreviousApartment', dataKey: uspsAddressSuggestions.addressExtra },
			{ questionKey: 'providePreviousCity', dataKey: uspsAddressSuggestions.city },
			{ questionKey: 'providePreviousState', dataKey: uspsAddressSuggestions.state },
		];
	}

	// Update the data in the application
	updateData(questionsAndAnswers, application);
}

// Trimming the secondaryAddress from the streetAddress (USPS includes the secondaryAddress (apt, unit, suite, etc.) at the end of streetAddress)
function stripTrailingSecondaryFromStreet(streetAddress: string, secondaryAddress: string): string {
	const secondary = (secondaryAddress ?? '').trim();
	if (!secondary) {
		return streetAddress ?? '';
	}
	const trimmedStreet = (streetAddress ?? '').trimEnd();
	const secLen = secondary.length;
	if (trimmedStreet.length < secLen) {
		return streetAddress ?? '';
	}
	const endSlice = trimmedStreet.slice(-secLen);
	if (endSlice.toLowerCase() !== secondary.toLowerCase()) {
		return streetAddress ?? '';
	}
	const beforeSuffix = trimmedStreet.slice(0, trimmedStreet.length - secLen);
	if (beforeSuffix.length > 0 && !/\s/.test(beforeSuffix[beforeSuffix.length - 1])) {
		return streetAddress ?? '';
	}
	const refined = beforeSuffix.trimEnd();
	return refined || trimmedStreet;
}

export async function expirationDateInThePast(expirationDate: string | Date, extraSaves: any, question: any, application: Application) {
	if (differenceInDays(expirationDate, getDateInstance()) <= 0) {
		$modal.open(modalCustomerApplicationInfo, {
			name: 'modalCustomerApplicationInfo',
			passedData: {
				title: 'Expiration Date in the Past',
				info: 'Please note you entered a date in the past',
				acceptText: 'OK',
			},
			backdrop: false,
		});
	}
}

export async function redirectToCustomerPortalInApp(data: any, extraSaves: any, question: any, application: Application) {
	settings.disableBrowserRedirectPopup = true;
	const linkForRedirect = await redirectToCustomerPortal(application.customerId);
	window.location.href = linkForRedirect;
}

export async function plateToVIN(state: any, extraSaves: any, question: any, application: Application) {
	const plateState = state;
	const plateNumberIndex = question.key.split('-')[1];
	let plateNumber = null;
	if (question.key.toLowerCase().includes('current')) {
		plateNumber = application.quoteAppData.applicationsDataAnswered.rebates.rebateCurrentVehicles.vehicles[plateNumberIndex].vehiclePlate;
	} else {
		plateNumber = application.quoteAppData.applicationsDataAnswered.rebates.rebateReturnedLeaseVehicles.vehicles[plateNumberIndex].vehiclePlate;
	}

	const response = await creditProviderAPI.plateToVIN(plateState, plateNumber);
	const index = question.key.split('-')[1];

	if (response.data?.payload?.vin) {
		await vinLookup(response.data.payload.vin, extraSaves, question, application);
	} else {
		if (question.key.toLowerCase().includes('current')) {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesYear-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesMake-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesModel-' + index).savedAnswer = null;
		} else {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesYear-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesMake-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesModel-' + index).savedAnswer = null;
		}
	}
}

export async function vinLookup(vin: string, extraSaves: any, question: any, application: Application) {
	const response = await vehicleAPI.vinLookup(vin);
	const index = question.key.split('-')[1];
	console.log('response', response);
	if (response && response.data) {
		if (question.key.toLowerCase().includes('current')) {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesYear-' + index).savedAnswer = `${response.data.payload.modelYear}`;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesMake-' + index).savedAnswer = `${response.data.payload.division}`;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesModel-' + index).savedAnswer = response.data.payload.modelName;
		} else {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesYear-' + index).savedAnswer = `${response.data.payload.modelYear}`;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesMake-' + index).savedAnswer = response.data.payload.division;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesModel-' + index).savedAnswer = response.data.payload.modelName;
		}
	} else {
		if (question.key.toLowerCase().includes('current')) {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesYear-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesMake-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateCurrentVehiclesModel-' + index).savedAnswer = null;
		} else {
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesYear-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesMake-' + index).savedAnswer = null;
			application.quoteAppData.questions.find((q: any) => q.key == 'rebateReturnedLeaseVehiclesModel-' + index).savedAnswer = null;
		}
	}
}

//export async function createNewApplication(value, extraSaves, question, application) {
//    // Only execute if the transaction is under a company and the applicant is the guarantor
//    if (application.isBusinessWithApplicantAsGuarantor()) {
//        // Create and save new lead
//        let lead = {
//            id: util.getGuid(),
//            firstName: application.preFilledData.firstName,
//            lastName: application.preFilledData.lastName,
//            fullName: application.preFilledData.firstName + ' ' + application.preFilledData.lastName,
//            phoneNumber: application.preFilledData.phoneNumber,
//            emailAddress: application.preFilledData.emailAddress,
//            dateOfBirth: application.preFilledData.dateOfBirth,
//            dateCreated: moment.utc().format()
//        }
//        console.log("LEAD OBJ", lead);
//        await api.customers.saveLead(lead)

//        // Create, populate, and save new Customer Application
//        let newApplicationObject = new Application()
//        newApplicationObject.isLatestApplication = true
//        newApplicationObject.dateCreated = moment.utc().format(DEFAULT_DATE_FORMAT_WITH_LONG_TIME)
//        newApplicationObject.editLinkExpiration = moment("0001-01-01", "yyyy-MM-dd").format(DEFAULT_DATE_FORMAT_WITH_LONG_TIME)
//        newApplicationObject.emailCodeExpiration = moment.utc().format(DEFAULT_DATE_FORMAT_WITH_LONG_TIME)
//        newApplicationObject.phoneCodeExpiration = moment.utc().format(DEFAULT_DATE_FORMAT_WITH_LONG_TIME)
//        newApplicationObject.id = util.getGuid()
//        newApplicationObject.customerId = util.getDefaultGuid()
//        newApplicationObject.leadId = lead.id
//        newApplicationObject.plaidId = application.plaidId
//        newApplicationObject.preFilledData = new ApplicationsPreFilledData(application.preFilledData)
//        newApplicationObject.quoteAppData.applicationsDataAnswered = new ApplicationsDataAnswered()
//        newApplicationObject.quoteAppData.applicationsDataAnswered.dealTypes = application.quoteAppData.applicationsDataAnswered.dealTypes
//        newApplicationObject.quoteAppData.applicationsDataAnswered.garagedAddress = application.quoteAppData.applicationsDataAnswered.garagedAddress
//        newApplicationObject.quoteAppData.applicationsDataAnswered.garagedAddressOutsideUS = application.quoteAppData.applicationsDataAnswered.garagedAddressOutsideUS
//        newApplicationObject.quoteAppData.applicationsDataAnswered.garagedAddressSameAsResidence = application.quoteAppData.applicationsDataAnswered.garagedAddressSameAsResidence
//        newApplicationObject.quoteAppData.applicationsDataAnswered.isBusiness = false
//        newApplicationObject.quoteAppData.applicationsDataAnswered.personalInfo = new ApplicationsPersonalInfo(application.quoteAppData.applicationsDataAnswered.personalInfo)
//        newApplicationObject.quoteAppData.applicationsDataAnswered.rebates = new ApplicationsRebates(application.quoteAppData.applicationsDataAnswered.rebates)
//        newApplicationObject.quoteAppData.currentQuestion = 'provideEstimateCreditScore'
//        newApplicationObject.quoteAppData.language = application.language

//        let newPathFollowedLastIndex = application.quoteAppData.pathFollowed.findIndex(key => key == 'isUnderBusiness')
//        newApplicationObject.quoteAppData.pathFollowed = application.quoteAppData.pathFollowed.slice(0, newPathFollowedLastIndex)

//        newApplicationObject.storeCode = application.storeCode
//        let applicationRequest = {
//            application: newApplicationObject,
//            isFinished: true,
//            isNewSession: true
//        }
//        await api.applications.saveNewApplication(applicationRequest)

//        // Add guarantor id to current business application and save it
//        application.guarantorApplicationId = newApplicationObject.id
//        api.applications.setGuarantorIdToCustomerApplication(application.id, application.guarantorApplicationId)

//        //await api.applications.saveApplication(currentApplicationRequest)

//        // Update local storage with the new application
//        const userData = util.encryptJSONText(JSON.stringify(newApplicationObject))
//        localStorage.setItem('applicationObject', userData)
//    }
//}

export async function reload() {
	window.location.reload();
}

export async function encryptTaxId(taxId: string, extraSaves: any, question: any, application: Application) {
	//if current taxId is not hashed
	if (!taxId.startsWith('*****')) {
		try {
			const response = await api.utilities.encrypt(taxId);
			if (response.data) {
				application.quoteAppData.applicationsDataAnswered.businessInfo.taxIdHashed = response.data.encryptedValue;
				application.quoteAppData.applicationsDataAnswered.businessInfo.taxIdLast4 = `*****${taxId.substring(taxId.length - 4)}`;
				question.savedAnswer = application.quoteAppData.applicationsDataAnswered.businessInfo.taxIdLast4;
			}
		} catch (error) {
			LogRocket.log('Error encrypting SSN', error);
		}
	}
}

export async function encryptSsn(ssn: any, extraSaves: any, question: any, application: Application) {
	//if current ssn is not hashed
	if (!ssn.startsWith('*****')) {
		application.setSNNType(ssn);
		try {
			if (ssn == null) {
				util.toastr('error', 'Error', 'Error adding SSN. Please refresh and try again.');
				LogRocket.log('No ssn value', ssn);
				throw new Error('no ssn value');
			}
			const response = await api.utilities.encrypt(ssn);

			if (response?.data?.encryptedValue) {
				application.quoteAppData.applicationsDataAnswered.personalInfo.ssnHashed = response.data.encryptedValue;
				application.quoteAppData.applicationsDataAnswered.personalInfo.ssnLast4 = `*****${ssn.substring(ssn.length - 4)}`;
				question.savedAnswer = application.quoteAppData.applicationsDataAnswered.personalInfo.ssnLast4;
			} else {
				util.toastr('error', 'Error', 'Error encrypting SSN. Please refresh and try again.');
				LogRocket.log('Failed to encrypt SSN in the backend');
				throw new Error('Failed to encrypt SSN in the backend');
			}
		} catch (error) {
			util.toastr('error', 'Error', 'Error encrypting SSN. Please refresh and try again.');
			LogRocket.log('Error encrypting SSN', error);
			throw new Error('error encrypting');
		}
	}
}
